import {Dispatch, SetStateAction, useEffect} from 'react';

import {isDevelopment} from '@/utils/environment';
import logError from '@/utils/errors/logError';

interface UseDevAutofillNamesParams {
  invitation: unknown;
  setFirstName: Dispatch<SetStateAction<string>>;
  setLastName: Dispatch<SetStateAction<string>>;
}

/**
 * Dev/testing-only autofill for the sign-up first/last name fields.
 *
 * Skips the invitation flow (invited co-parents already have a name) and
 * lazily loads `@faker-js/faker` via a dynamic import, so it's never bundled
 * into the production build (see `@/utils/randomNameGenerator`).
 *
 * Uses the functional `setState` updater form so a name typed by the user
 * (or loaded from elsewhere) while the chunk is still downloading is never
 * clobbered once the import resolves.
 */
export function useDevAutofillNames({
  invitation,
  setFirstName,
  setLastName,
}: UseDevAutofillNamesParams) {
  useEffect(() => {
    if (invitation || !isDevelopment) return undefined;

    let isMounted = true;

    import('@/utils/randomNameGenerator')
      .then(({generateRandomFirstName, generateRandomLastName}) => {
        if (!isMounted) return;
        setFirstName(prev => prev || generateRandomFirstName());
        setLastName(prev => prev || generateRandomLastName());
      })
      .catch(error => {
        // Dev-only autofill: never block sign-up over a failed chunk load.
        logError(error, {
          fileName: 'useDevAutofillNames.ts',
          functionName: 'useDevAutofillNames',
          customTitle:
            'SignUp - useDevAutofillNames - failed to load dev autofill names',
        });
      });

    return () => {
      isMounted = false;
    };
  }, [invitation, setFirstName, setLastName]);
}
